import type { JSX, ParentComponent } from "solid-js";
import { Show, splitProps } from "solid-js";
import { Dynamic } from "solid-js/web";
import "./Pill.css";

type Props = {
	as?: "span" | "button";
	id?: string;
	theme?:
		| "ink"
		| "success"
		| "warning"
		| "error"
		| "info"
		| "dark"
		| "purple"
		| "grey900"
		| "grey600"
		| "grey200"
		| "primary"
		| "primary-dark"
		| "document"
		| "other"
		| "muted";
	variant?: "filled" | "outline" | "dotted" | "contrast";
	/** Draw a border tinted from the pill's foreground colour. */
	border?: boolean;
	size?: "xsmall" | "small" | "standard";
	class?: string;
	style?: JSX.CSSProperties;
	/** Show a leading dot for the status badge. */
	leadingDot?: boolean;
	/** Native tooltip (e.g. account name). */
	title?: string;
	/** Click handler. When set, the pill shows a pointer cursor (see `gvtui-pill--clickable`). */
	onClick?: JSX.EventHandlerUnion<HTMLElement, MouseEvent>;
	onFocus?: JSX.EventHandlerUnion<HTMLElement, FocusEvent>;
	onKeyDown?: JSX.EventHandlerUnion<HTMLElement, KeyboardEvent>;
	disabled?: boolean;
	role?: JSX.AriaAttributes["role"];
	/** Accessible name, for a clickable pill whose content is glyphs or numbers. */
	"aria-label"?: string;
	"aria-selected"?: boolean;
	/** On/off state, for a button pill that toggles something. */
	"aria-pressed"?: boolean;
	tabIndex?: number;
	"data-value"?: string | number;
};

/**
 * Label badges for status and categories with theme and variant options.
 *
 * [Design System →](http://localhost:5174/component/Pill)
 */
export const Pill: ParentComponent<Props> = (props) => {
	const [local, rest] = splitProps(props, [
		"children",
		"theme",
		"size",
		"class",
		"variant",
		"border",
		"leadingDot",
		"as",
		"disabled",
	]);
	const component = () => local.as ?? "span";

	return (
		<Dynamic
			component={component()}
			class={`gvtui-pill ${local.class ?? ""}`}
			classList={{
				[`gvtui-pill--${local.theme}`]: !!local.theme,
				[`gvtui-pill--outline-${local.theme}`]:
					!!local.theme && local.variant === "outline",
				[`gvtui-pill--dotted-${local.theme}`]:
					!!local.theme && local.variant === "dotted",
				"gvtui-pill--outline": !local.theme && local.variant === "outline",
				"gvtui-pill--dotted": local.variant === "dotted",
				"gvtui-pill--contrast": local.variant === "contrast",
				"gvtui-pill--border":
					(!!local.border || local.variant === "outline") &&
					local.variant !== "dotted" &&
					local.variant !== "contrast",
				[`gvtui-pill--${local.size}`]:
					!!local.size && local.size !== "standard",
				"gvtui-pill--button": component() === "button",
			}}
			role={component() === "span" ? "status" : undefined}
			type={component() === "button" ? "button" : undefined}
			disabled={component() === "button" ? local.disabled : undefined}
			{...rest}
		>
			<Show when={local.leadingDot}>
				<span class="gvtui-pill__leading-dot" />
			</Show>
			{local.children}
		</Dynamic>
	);
};
